import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { getStoredUser } from "@/lib/auth";
import { useSessions, useRoutines, type Session } from "@/hooks/useApi";
import WaypointLogo from "@/components/WaypointLogo";
import { cn } from "@/lib/utils";

const WEEK_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const CAL_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const pad = (n: number) => String(n).padStart(2, "0");

// ── Week strip ──────────────────────────────────────────────────────

function WeekStrip({ sessions }: { sessions: Session[] }) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const dow = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dow + 6) % 7));

  const sessionDays = useMemo(
    () =>
      new Set(
        sessions
          .filter((s) => s.completed_at)
          .map((s) => s.started_at.slice(0, 10)),
      ),
    [sessions],
  );

  return (
    <div className="flex justify-between">
      {Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const key = d.toISOString().slice(0, 10);
        const hasSession = sessionDays.has(key);
        const isToday = key === todayStr;
        const isFuture = key > todayStr;

        return (
          <div key={key} className="flex flex-col items-center gap-1.5">
            <span
              className={cn(
                "text-xs",
                isToday ? "text-zinc-200" : "text-zinc-500",
              )}
            >
              {WEEK_LABELS[i]}
            </span>
            <div
              className={cn(
                "h-6 w-6 rounded-full",
                hasSession
                  ? "bg-brand"
                  : isToday
                    ? "ring-2 ring-brand/50"
                    : isFuture
                      ? "bg-zinc-900"
                      : "bg-zinc-800",
              )}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Calendar ────────────────────────────────────────────────────────

function SessionCalendar({
  sessions,
  onSelect,
}: {
  sessions: Session[];
  onSelect: (id: string) => void;
}) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const byDay = useMemo(() => {
    const map = new Map<string, Session[]>();
    for (const s of sessions) {
      if (!s.completed_at) continue;
      const key = s.started_at.slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return map;
  }, [sessions]);

  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDow = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);

  const cells: (number | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setMonth(new Date(year, mo - 1, 1))}
          className="p-1 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium">
          {month.toLocaleString("default", { month: "long", year: "numeric" })}
        </span>
        <button
          onClick={() => setMonth(new Date(year, mo + 1, 1))}
          className="p-1 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1 text-center text-xs text-zinc-500">
        {CAL_DAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const key = `${year}-${pad(mo + 1)}-${pad(day)}`;
          const daySessions = byDay.get(key) ?? [];
          const isToday = key === today;
          const hasSessions = daySessions.length > 0;

          return (
            <div key={i} className="flex items-center justify-center py-0.5">
              <button
                disabled={!hasSessions}
                onClick={() => onSelect(daySessions[daySessions.length - 1].id)}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors",
                  hasSessions && isToday
                    ? "bg-brand text-zinc-950 font-bold cursor-pointer hover:bg-brand/90"
                    : hasSessions
                      ? "bg-brand/20 text-brand font-medium hover:bg-brand/30 cursor-pointer"
                      : isToday
                        ? "ring-2 ring-brand/50 text-zinc-100 cursor-default"
                        : "text-zinc-500 cursor-default",
                )}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const { data: sessions = [] } = useSessions();
  const { data: routines = [] } = useRoutines();

  const inProgress = sessions.find((s) => !s.completed_at);

  const completed = useMemo(
    () =>
      [...sessions]
        .filter((s) => s.completed_at)
        .sort((a, b) => b.started_at.localeCompare(a.started_at)),
    [sessions],
  );

  const lastCompleted = completed[0] ?? null;

  const now = new Date();
  const monthKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const thisMonthCount = completed.filter((s) =>
    s.started_at.startsWith(monthKey),
  ).length;

  const daysSince = lastCompleted
    ? Math.floor(
        (Date.now() - new Date(lastCompleted.started_at).getTime()) / 86400000,
      )
    : null;
  const sinceLabel =
    daysSince === null
      ? null
      : daysSince === 0
        ? "today"
        : daysSince === 1
          ? "yesterday"
          : `${daysSince}d ago`;

  return (
    <div className="p-4 space-y-5">
      {/* Greeting + stats + week strip */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl font-semibold">
            Hey, {user?.name?.split(" ")[0]}
          </h1>
          {sessions.length > 0 && (
            <p className="text-xs text-zinc-500">
              <span className="text-zinc-300">{thisMonthCount}</span> this month
              {sinceLabel && (
                <>
                  {" "}
                  · <span className="text-zinc-300">{sinceLabel}</span>
                </>
              )}
            </p>
          )}
        </div>
        {sessions.length > 0 && <WeekStrip sessions={sessions} />}
      </div>

      {/* Primary CTA */}
      {inProgress ? (
        <button
          onClick={() => navigate(`/session/${inProgress.id}`)}
          className="flex w-full items-center justify-between rounded-lg border border-green-800/50 bg-green-900/20 px-4 py-3 text-sm hover:bg-green-900/30 transition-colors"
        >
          <span className="font-medium">{inProgress.name}</span>
          <span className="rounded-full bg-green-900/40 px-2 py-0.5 text-xs text-green-400">
            Continue
          </span>
        </button>
      ) : (
        <button
          onClick={() => navigate("/session/new")}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-semibold text-zinc-950 hover:bg-brand/80 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Start Workout
        </button>
      )}

      {/* Last session — hidden while a session is in progress */}
      {!inProgress && lastCompleted && (
        <button
          onClick={() => navigate(`/session/${lastCompleted.id}`)}
          className="flex w-full items-center justify-between rounded-lg border border-zinc-800 px-3 py-2.5 hover:border-zinc-700 transition-colors"
        >
          <div className="text-left">
            <p className="text-xs text-zinc-500">Last session</p>
            <p className="text-sm font-medium">{lastCompleted.name}</p>
          </div>
          <div className="flex items-center gap-1 text-xs text-zinc-500">
            {new Date(lastCompleted.started_at).toLocaleDateString("default", {
              month: "short",
              day: "numeric",
            })}
            <ChevronRight className="h-3 w-3" />
          </div>
        </button>
      )}

      {/* Calendar */}
      {sessions.length > 0 && (
        <SessionCalendar
          sessions={sessions}
          onSelect={(id) => navigate(`/session/${id}`)}
        />
      )}

      {/* Empty state */}
      {routines.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center">
          <WaypointLogo className="mx-auto mb-2 h-8 w-8 text-zinc-600" />
          <p className="text-sm text-zinc-500">No routines yet</p>
          <button
            onClick={() => navigate("/routines/new")}
            className="mt-2 text-sm text-zinc-300 underline underline-offset-2"
          >
            Create your first routine
          </button>
        </div>
      )}
    </div>
  );
}
